import { Link, useNavigate } from '@solidjs/router';
import { Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { COUNTRY_SELECTOR_TRIGGER_ATTR, localeMenuIsOpen, toggleCountrySelectionMenu } from '../../stores/locale-store';
import { SEARCH_PANEL_TRIGGER_ATTR, searchPanelIsOpen, toggleSearchPanel } from '../../stores/search-store';
import { UserControls } from '../login-controls/login-controls';
import { createUserAccount } from '../login-controls/create-user-account';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import { HeartCviIcon } from '../icons-library/heart-cvi';
import { SearchCviIcon } from '../icons-library/search-cvi';
import { BasketCviIcon } from '../icons-library/basket-cvi';
import { GlobeCviIcon } from '../icons-library/globe-cvi';

export const HeaderNavigationShortcuts = () => {
    const { appStore, viewport, searchPage, shopPage, pageRegistration, userProfileUrls, siteInfo, localize } = useContext(AppContext);
    const { close } = appStore;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    const navigate = useNavigate();
    const { loggedIn, shouldShowLoginAffordance } = createUserAccount();

    const ghostButton = 'icon-button-ghost icon-button-ghost-md';
    const showSecondaryIcons = () => !isOriginalTablet();

    return (
        <div class="flex items-center gap-cvi-6">
            <Show when={showSecondaryIcons() && siteInfo.siteType === 'default'}>
                <button
                    class={ghostButton}
                    onClick={() => {
                        close();
                        navigate(userProfileUrls.bookmarks);
                    }}
                    aria-label={localize('header_my_content_icon', 'Link to My content')}
                >
                    <HeartCviIcon size="medium" />
                </button>
            </Show>

            <Show when={showSecondaryIcons() && shopPage.url && shopPage.url !== ''}>
                <Link
                    class={ghostButton}
                    href={removeTrailingSlash(shopPage.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={localize('header_shop_icon', 'Link to Shop')}
                >
                    <BasketCviIcon size="medium" />
                </Link>
            </Show>

            {/* Opens the search band under the header rather than the search page; the band
                takes the user there on submit. */}
            <Show when={searchPage?.url && searchPage.url !== ''}>
                <button
                    class={ghostButton}
                    onClick={() => {
                        close();
                        toggleSearchPanel();
                    }}
                    aria-label={localize('header_search_icon', 'Search')}
                    aria-expanded={searchPanelIsOpen()}
                    {...{ [SEARCH_PANEL_TRIGGER_ATTR]: '' }}
                >
                    <SearchCviIcon size="medium" />
                </button>
            </Show>

            <Show when={showSecondaryIcons()}>
                <button
                    class={ghostButton}
                    onClick={() => {
                        close();
                        toggleCountrySelectionMenu();
                    }}
                    tabIndex="0"
                    aria-label={localize('header_country_selection_icon', 'Open country selection menu')}
                    classList={{ '[&_svg]:fill-primary-hover': localeMenuIsOpen() }}
                    /* The country selector lays its close button over this exact spot. */
                    {...{ [COUNTRY_SELECTOR_TRIGGER_ATTR]: '' }}
                >
                    <GlobeCviIcon size="medium" />
                </button>
            </Show>

            {/* Gate the wrapper on whether UserControls would actually render something — otherwise
                the empty flex row + 8px spacer still consume the parent's gap-cvi-6, leaving dead
                space to the right of the globe (PAC patient sites: no login affordance by design). */}
            <Show when={showSecondaryIcons() && pageRegistration.url && pageRegistration.url !== '' && (loggedIn() || shouldShowLoginAffordance())}>
                <div class='flex flex-row'>
                    <div class='w-cvi-4' />
                    <UserControls showLoginRegisterLabel={!isOriginalTablet()} variant="ghost" />
                </div>
            </Show>
        </div>
    );
};
